<script lang="ts">
    import vm from '@journeys/Journeys.viewmodel';
    import { computed, inject, onMounted, onUnmounted, ref, watch, WatchStopHandle } from 'vue';
    import { cancelFrame, requestFrame } from '@utils';
    import { serializeStepSegments, stepList } from '@journeys/utils/journeySerialization';
    import RecurringEditor from '@cwd/shared/components/RecurringEditor/RecurringEditor.vue';
    import Notification from '@shared/components/Alerts/Notification.vue';
    import FluidButton from '@shared/components/FluidButton.vue';
    import StepCard from './StepCard.vue';
    import CountDisplay from '@shared/components/CountDisplay.vue';
    import { JourneyStatus } from '@journeys/Enums';
    import { debounce, deepClone } from '../../../../shared/utils';
    import ScrollableContainer from '@shared/components/ScrollableContainer.vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { FeatureService } from '@shared/directives/Feature.service';
    import { UserEventService } from '@shared/Root/UserEvent.service';
    import ExecutionHistory from '@journeys/components/ExecutionHistory.vue';
    import { state, setupToasts, inspectToastTarget, handleResponse, categories } from './Publish';
    import { api } from '@cwd/lib/main';
    import { hasAccessLevel } from '@cwd/Acl/useAclAccess';
    import { AccessControlLevel } from '@cwd/Acl/Acl.access';

    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'Menu_PublishJourney',
    };

    export default {
        name: component.name,
    };
</script>

<script lang="ts" setup>
    const { editor, activeJourney, account } = vm;
    const { conditionToasts } = setupToasts();
    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);
    const userEventService = injectorAccess.injector.get(UserEventService);
    // Uses the server-computed CurrentUserAccessLevel (group-aware) -- see SidebarMain.vue's canWrite
    // for why this replaced hasAclWriteAccess's raw ACL scan.
    const canWrite = computed(() => hasAccessLevel(activeJourney.CurrentUserAccessLevel, AccessControlLevel.Write));
    const isPublishable = ref(activeJourney.Status);

    onMounted(() => {
        account.listings.get('/issues/listing');
        account.listings.get('/textmessages/listing');
        account.listings.get('/segments');
        api.get('/Categories').then((data: any[]) => (categories.value = data));

        watch(
            () => activeJourney.Status,
            (newStatus: number) => {
                setTimeout(() => {
                    isPublishable.value = newStatus;
                    resetStates();
                }, 2500);
            }
        );
    });

    onUnmounted(() => {
        if (publishJourneyMouseUpListener) {
            document.removeEventListener('mouseup', publishJourneyMouseUpListener);
            publishJourneyMouseUpListener = undefined;
        }

        if (endJourneyMouseUpListener) {
            document.removeEventListener('mouseup', endJourneyMouseUpListener);
            endJourneyMouseUpListener = undefined;
        }

        clearTimeout(publishJourneyPatienceTimer);
        clearTimeout(endJourneyPatienceTimer);
        cancelFrame(bounceFrameReq);
    });

    const patienceTime = 2500;

    const publishJourneyStateButtonIsPressed = ref(false);
    const publishJourneyStateProgressDone = ref(false);

    let publishJourneyMouseUpListener: (() => void) | undefined;
    let endJourneyMouseUpListener: (() => void) | undefined;

    let publishJourneyPatienceTimer: NodeJS.Timeout | undefined;
    let endJourneyPatienceTimer: NodeJS.Timeout | undefined;

    const endJourneyStateButtonIsPressed = ref(false);
    const endJourneyStateProgressDone = ref(false);

    const publishJourney = async () => {
        editor.history.cache.safeguard = deepClone(activeJourney);
        publishJourneyStateButtonIsPressed.value = true;
        clearTimeout(publishJourneyPatienceTimer);

        // Remove any existing mouseup listener before adding a new one
        if (publishJourneyMouseUpListener) {
            document.removeEventListener('mouseup', publishJourneyMouseUpListener);
        }

        const mouseUpHandler = () => {
            clearTimeout(publishJourneyPatienceTimer);
            publishJourneyStateButtonIsPressed.value = false;
            publishJourneyMouseUpListener = undefined;
        };

        publishJourneyMouseUpListener = mouseUpHandler;
        document.addEventListener('mouseup', mouseUpHandler, { once: true });

        publishJourneyPatienceTimer = setTimeout(() => {
            publishJourneyStateButtonIsPressed.value = false;
            publishJourneyStateProgressDone.value = true;
            publishJourneyMouseUpListener = undefined;

            serializeStepSegments();

            userEventService.registerUserEvent('Journey', 'Published journey', null);
            editor.events.emit('re_saveselectedschedule');
        }, patienceTime);
    };

    const confirmedStopJourney = ref(false);
    const stoppingJourney = ref(false);

    const confirmStopJourney = () => {
        confirmedStopJourney.value = true;
    };

    const stopJourney = () => {
        stoppingJourney.value = true;
        editor.events.emit('re_disableactiveschedule');
        userEventService.registerUserEvent('Journey', 'Stopped journey', null);
    };

    const endJourney = async () => {
        endJourneyStateButtonIsPressed.value = true;
        clearTimeout(endJourneyPatienceTimer);

        // Remove any existing mouseup listener before adding a new one
        if (endJourneyMouseUpListener) {
            document.removeEventListener('mouseup', endJourneyMouseUpListener);
        }

        const mouseUpHandler = () => {
            clearTimeout(endJourneyPatienceTimer);
            endJourneyStateButtonIsPressed.value = false;
            endJourneyMouseUpListener = undefined;
        };

        endJourneyMouseUpListener = mouseUpHandler;
        document.addEventListener('mouseup', mouseUpHandler, { once: true });

        endJourneyPatienceTimer = setTimeout(() => {
            endJourneyStateButtonIsPressed.value = false;
            endJourneyStateProgressDone.value = true;
            endJourneyMouseUpListener = undefined;

            editor.events.emit('re_endactiveschedule');
            userEventService.registerUserEvent('Journey', 'Ended journey', null);
        }, patienceTime);
    };

    const resetStates = () => {
        confirmedStopJourney.value = false;
        stoppingJourney.value = false;

        publishJourneyStateButtonIsPressed.value = false;
        publishJourneyStateProgressDone.value = false;

        endJourneyStateButtonIsPressed.value = false;
        endJourneyStateProgressDone.value = false;
    };

    let bounce = true,
        bounceFrameReq = 0,
        journeyWatcher: WatchStopHandle,
        pageWatcher = watch(
            () => [editor.state.activeMenu, editor.state.showNodeMenu],
            ([activeMenu, nodeMenu]: (string | boolean | null)[]) => {
                if (!nodeMenu && activeMenu === 'publish') {
                    serializeStepSegments();

                    journeyWatcher = watch(
                        [activeJourney.nodes, activeJourney.lines],
                        () =>
                            !editor.state.busy &&
                            debounce(
                                () => {
                                    cancelFrame(bounceFrameReq);
                                    bounceFrameReq = requestFrame(() => {
                                        if ((bounce = !bounce)) serializeStepSegments();
                                    });
                                },
                                { delay: 400 }
                            )
                    );
                } else journeyWatcher && journeyWatcher();
            }
        );

    const triggerState = () => (state.reactive_exp = !state.reactive_exp);
    editor.events.addListener('triggerstate', triggerState);

    const pageState = ref('steps');
    const setPageState = (state: string, callback?: Func) => {
        if (state === 'schedule') {
            editor.events.emit('re_croneditor', 'edit', activeJourney.scheduleController);
        } else {
            editor.events.emit('triggerstate');
        }

        callback && callback();
        pageState.value = state;
    };

    const disablePublishJourney = computed(() => {
        return state.computingToasts || conditionToasts.value.some((toast) => toast.type === 'error') || publishJourneyStateProgressDone.value;
    });

    const disableScheduleCallback = () => {
        activeJourney.Status = JourneyStatus.Draft;
    };

    const endScheduleCallback = () => {
        activeJourney.Status = JourneyStatus.Ended;
    };

    const handleScheduleFail = () => {
        resetStates();
    };
</script>

<template>
    <ScrollableContainer>
        <h2 class="h3">{{ $Resources.ScheduleJourneyHeading }}</h2>

        <RecurringEditor
            v-model:start="activeJourney.scheduleController.Scheduled"
            :ownerId="activeJourney.JourneyId!"
            :disabled="activeJourney.Status === JourneyStatus.Active"
            :ownerName="activeJourney.Title"
            :scheduleController="activeJourney.scheduleController"
            :steps="stepList"
            :scheduleCallback="handleResponse"
            :scheduleFailCallback="handleScheduleFail"
            :handleSchedule="() => setPageState('schedule')"
            :showExecutionHistoryButton="state.openExecutionHistory"
            :disableScheduleCallback="disableScheduleCallback"
            :endScheduleCallback="endScheduleCallback"
            @openExecutionHistory="state.openExecutionHistory = !state.openExecutionHistory" />

        <div v-if="pageState === 'schedule'" class="form-group m-sm-b">
            <FluidButton
                :text="$Resources.CloseButton"
                class="btn btn-default btn-block"
                @click="
                    setPageState('steps', () => {
                        editor.events.emit('re_croneditor', 'save');
                        editor.events.emit('re_croneditor', 'close');
                    })
                "></FluidButton>
        </div>

        <template v-if="pageState === 'steps'">
            <div class="m-sm-b">
                <transition-group name="cards">
                    <Notification
                        v-for="({ key, type, message, node_id }, idx) of conditionToasts"
                        :key="key"
                        :style="`--idx: ${idx}`"
                        :type="type"
                        class="static"
                        :message="message"
                        sizing="small"
                        :inspect="node_id ? () => inspectToastTarget(node_id) : undefined" />
                </transition-group>
            </div>

            <CountDisplay :activeJourney="activeJourney" :stepList="stepList"></CountDisplay>

            <StepCard
                v-for="step of stepList"
                :key="step.data.Name + step.chain.flat().join('-')"
                :step="step"
                :journeyIsDraft="activeJourney.Status === JourneyStatus.Draft" />
        </template>
    </ScrollableContainer>

    <div v-show="pageState !== 'schedule'" class="static-container bottom">
        <Notification
            v-if="activeJourney.Status === JourneyStatus.Template"
            type="error"
            class="static"
            :message="{ head: $Resources.ErrorTitleLabel, body: $Resources.JourneyCantPublishTemplateErrorBody, break: true }"
            sizing="small" />

        <div class="d-flex flex-direction-col gap-xs m-sm-t">
            <template v-if="featureAccess.hasFeature('Automation.Journeys')">
                <template v-if="isPublishable === JourneyStatus.Draft || isPublishable === JourneyStatus.Ended">
                    <div class="p-relative">
                        <FluidButton
                            class="btn btn-cta btn-block"
                            :disabled="disablePublishJourney || !canWrite"
                            :text="activeJourney.startingNode ? $Resources.JourneyStartLabel : $Resources.JourneyNotReadyLabel"
                            @mousedown.left="publishJourney" />
                        <span
                            class="btn-hold-progress p-absolute"
                            :class="{ active: publishJourneyStateButtonIsPressed, done: publishJourneyStateProgressDone }"></span>
                    </div>
                </template>

                <template v-if="isPublishable === JourneyStatus.Active">
                    <FluidButton
                        v-if="!confirmedStopJourney"
                        class="btn btn-subtle btn-block"
                        :text="$Resources.JourneyStopLabel"
                        :disabled="endJourneyStateProgressDone || stoppingJourney"
                        @click="confirmStopJourney" />

                    <FluidButton
                        v-if="confirmedStopJourney"
                        class="btn btn-warning btn-block"
                        :text="$Resources.JourneyConfirmStopLabel"
                        :disabled="endJourneyStateProgressDone || stoppingJourney || !canWrite"
                        @click="stopJourney" />

                    <FluidButton
                        v-if="confirmedStopJourney"
                        class="btn btn-subtle btn-block"
                        :text="$Resources.CancelButton"
                        :disabled="endJourneyStateProgressDone || stoppingJourney"
                        @click="confirmedStopJourney = false" />

                    <div class="p-relative" v-if="!confirmedStopJourney">
                        <FluidButton
                            class="btn btn-default btn-block"
                            :disabled="endJourneyStateProgressDone || !canWrite"
                            :text="$Resources.JourneyEndLabel"
                            @mousedown.left="endJourney" />
                        <span
                            class="btn-hold-progress p-absolute"
                            :class="{ active: endJourneyStateButtonIsPressed, done: endJourneyStateProgressDone }"></span>
                    </div>
                </template>
            </template>
            <template v-else>
                <FluidButton class="btn btn-cta btn-block" disabled="true" :text="$Resources.JourneyNotReadyLabel" />
            </template>
        </div>
    </div>

    <ExecutionHistory :journeyId="activeJourney.JourneyId" :show="state.openExecutionHistory" @close="state.openExecutionHistory = false" />
</template>

<style lang="less">
    .btn-hold-progress {
        transition: opacity 250ms ease-in-out;
        opacity: 0;
        background-color: var(--brand-secondary-100);
        border-radius: var(--border-radius);
        right: 0;
        bottom: -10px;
        left: 0;
        overflow: hidden;

        &:after {
            content: '';
            display: block;
            width: 0%;
            height: 5px;
            background-color: var(--brand-success);
            box-shadow: 1px 0 0 rgba(255, 255, 255, 0.25);
        }

        &.active,
        &.done {
            &:after {
                width: 100%;
                transition: width 2500ms ease-in-out;
            }
        }
    }

    .btn:hover + .btn-hold-progress,
    .btn-hold-progress.done {
        opacity: 1;
    }

    .step-container {
        padding: var(--ug-spacing-md) var(--ug-spacing-md) var(--ug-spacing-xs);
        border-radius: var(--border-radius);
        border: 1px solid var(--border-color);
        border-radius: var(--border-radius);
        margin-bottom: var(--ug-spacing-lg);
        transition: border-color, box-shadow, outline;
        transition-duration: 0.2s;
        transition-timing-function: ease-out;
    }

    .step-container:hover {
        border: 1px solid var(--brand-primary-500);
    }

    .notification-wrapper {
        --toast-height: 200px;
    }

    .cards-enter-active,
    .cards-leave-active {
        transition-property: max-height transform opacity;
        transition-duration: 1s, 0.6s, 0.5s;
        transition-timing-function: ease-out;
        transition-delay: calc(var(--idx) * 0.25s);
    }

    .cards-enter-from {
        max-height: var(--toast-height);
        opacity: 0;
    }

    .cards-enter-to {
        max-height: var(--toast-height);
        opacity: 1;
    }

    .cards-leave-from {
        max-height: var(--toast-height);
        opacity: 1;
    }

    .cards-leave-to {
        max-height: 0;
        transform: scale(0.85);
        opacity: 0;
    }

    .card-section.fl {
        display: flex;
        flex-direction: row;
    }
</style>
